import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CLAIM_STATUSES } from "@/lib/hyundaiFields";
import { HYUNDAI } from "@/lib/portalBrands";
import { AlertTriangle, CheckCircle2, Plus, Trash2 } from "lucide-react";

const Sel = ({ value, onChange, options, testid }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const Errors = ({ errors, system }) => errors.length === 0 ? (
  <p className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2.5 text-sm text-emerald-300" data-testid="hy-claim-no-errors">
    <CheckCircle2 className="h-4 w-4" /> No return risks found - ready for {system}
  </p>
) : (
  <ul className="space-y-1 rounded-md border border-red-500/40 bg-red-500/10 p-2.5 text-sm text-red-200" data-testid="hy-claim-errors">
    {errors.map((e, i) => <li key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{e}</li>)}
  </ul>
);

const Parts = ({ parts, set, causal }) => (
  <div className="space-y-2">
    <Label>Replacement Part # / Quantity{causal ? " / Causal part" : ""}</Label>
    {parts.map((p, i) => (
      <div key={i} className="flex items-center gap-2">
        <Input value={p.part_number} onChange={(e) => set(parts.map((x, j) => j === i ? { ...x, part_number: e.target.value } : x))} data-testid={`hy-part-number-${i}`} />
        <Input value={p.qty} onChange={(e) => set(parts.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} className="w-20" data-testid={`hy-part-qty-${i}`} />
        {causal && <input type="radio" name="causal-part" checked={!!p.causal} title="Causal part" onChange={() => set(parts.map((x, j) => ({ ...x, causal: j === i })))} data-testid={`hy-part-causal-${i}`} />}
        <Button variant="ghost" size="icon" onClick={() => set(parts.filter((_, j) => j !== i))} data-testid={`hy-part-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => set([...parts, { part_number: "", qty: 1 }])} data-testid="hy-part-add" className="gap-1"><Plus className="h-3.5 w-3.5" /> Add part</Button>
  </div>
);

export function ClaimEditor({ claim, techs, advisors = [], onClose, onSaved, cfg = HYUNDAI }) {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState({});
  useEffect(() => { setD(claim ? { ...claim, parts: claim.parts || [] } : null); }, [claim]);
  useEffect(() => { if (cfg.codesApi) api.get(cfg.codesApi).then(({ data }) => setCodes(data)).catch(() => {}); }, [cfg.codesApi]);
  if (!d) return null;
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { id, errors, status, created_at, updated_at, owner_id, ...data } = d;
      const { data: saved } = await api.patch(`${cfg.api}/claims/${id}`, { data, status });
      onSaved(saved);
      setD({ ...saved, parts: saved.parts || [] });
      toast.success(saved.errors.length ? `Saved - ${saved.errors.length} issue(s) left` : "Saved - no issues left");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!claim} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="hy-claim-editor">
        <DialogHeader>
          <DialogTitle>{cfg.cols.idLabel} {d[cfg.cols.id]} - RO {d[cfg.cols.ro] || "?"}{d.line ? ` line ${d.line}` : ""}</DialogTitle>
          <DialogDescription>{d.source_filename} - AI drafted. Fix anything flagged, then save.</DialogDescription>
        </DialogHeader>
        <Errors errors={d.errors || []} system={cfg.system} />
        <div className="grid gap-3 sm:grid-cols-3">
          <div><Label>{cfg.key === "gm" ? "Transaction type" : "Claim type"}</Label><Sel value={d[cfg.typeKey]} onChange={set(cfg.typeKey)} options={cfg.types.map((t) => [t, t])} testid="hy-field-claim_type" /></div>
          <div><Label>Technician</Label><Sel value={d.technician_id} onChange={set("technician_id")} testid="hy-field-technician"
            options={[["", d.tech_hint ? `Pick one (RO says: ${d.tech_hint})` : "Pick a technician"], ...techs.filter((t) => !t.dealership_id || t.dealership_id === d.dealership_id || t.id === d.technician_id).map((t) => [t.id, `${t.name}${t.number ? ` (${t.number})` : ""}`])]} /></div>
          {cfg.advisors && <div><Label>Service advisor</Label><Sel value={d.advisor_number} onChange={set("advisor_number")} testid="gm-field-advisor"
            options={[["", d.advisor_hint ? `Pick one (RO says: ${d.advisor_hint})` : "Pick an advisor"],
              ...advisors.filter((a) => a.dealership_id === d.dealership_id).map((a) => [a.gmin, `${a.name} (GMIN ${a.gmin})`]),
              ...(d.advisor_number && !advisors.some((a) => a.dealership_id === d.dealership_id && a.gmin === d.advisor_number) ? [[d.advisor_number, `GMIN ${d.advisor_number}`]] : [])]} /></div>}
          <div><Label>Status</Label><Sel value={d.status} onChange={set("status")} options={[...CLAIM_STATUSES, "saved"].map((s) => [s, s === "saved" ? `Saved in ${cfg.system}` : s[0].toUpperCase() + s.slice(1)])} testid="hy-field-status" /></div>
          <div><Label>{cfg.yesNo[1]}</Label><Sel value={d[cfg.yesNo[0]]} onChange={set(cfg.yesNo[0])} options={[["No", "No"], ["Yes", "Yes"]]} testid={`hy-field-${cfg.yesNo[0]}`} /></div>
        </div>
        {["complaint", "cause"].map((k) => codes[k] && (
          <datalist key={k} id={`dl-${k}`}>{codes[k].map((c) => <option key={c.code} value={c.code}>{c.description}</option>)}</datalist>
        ))}
        {cfg.groups.map((g) => (
          <div key={g.title}>
            <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{g.title}</p>
            <div className="grid gap-3 sm:grid-cols-4">
              {g.fields.map(([k, label, dl]) => (
                <div key={k}><Label className="text-xs">{label}</Label><Input list={dl ? `dl-${dl}` : undefined} value={d[k] || ""} onChange={(e) => set(k)(e.target.value)} data-testid={`hy-field-${k}`} />
                  {dl && codes[dl] && <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{codes[dl].find((c) => c.code === d[k])?.description || "Not a valid GM code"}</p>}</div>
              ))}
            </div>
          </div>
        ))}
        <Parts parts={d.parts} set={set("parts")} causal={cfg.causal} />
        {cfg.story.map(([k, label]) => (
          <div key={k}>
            <Label className="text-xs">{label} <span className="text-muted-foreground">({(d[k] || "").length} characters)</span></Label>
            <Textarea rows={3} value={d[k] || ""} onChange={(e) => set(k)(e.target.value)} data-testid={`hy-field-${k}`} />
          </div>
        ))}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="hy-claim-close">Close</Button>
          <Button onClick={save} disabled={saving} data-testid="hy-claim-save">{saving ? "Saving..." : "Save claim"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
